import AddButton from "../../../../components/AddButton";
import { IHVAC, ISecurityDetails } from "../types";
import Modal from "../../../../components/Modal";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  HvacAddState,
  HvacSaving,
  SecurityAddState,
  SecurityEditState,
  HvacModalState,
  HvacForEdit,
  hvacDeactivateModal,
} from "../atoms/atoms";
import { useEffect, useState } from "react";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { useQueryClient } from "react-query";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import CancelButton from "../../../../components/CancelButton";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import MinusButton from "../../../../components/MinusButton";
import PlusButton from "../../../../components/PlusButton";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import { useVendors } from "../hooks/useVendors";
import useAddSingleHvac from "../hooks/useAddSingleHvac";
import { toast } from "../../../../components/toastNotification";
import { useGetHvacsByRecordNumber } from "../hooks/useGetHvacsByRecordNumber";
import HvacCard from "../../../../components/HvacCard";
import CheckBoxLabelHookForm from "../../../../components/CheckBoxLabelHookForm";
import useEditSingleHvac from "../hooks/useEditSingleHvac";
import useDeleteHvac from "../hooks/useDeleteHvac";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import CloseIconButton from "../../../../components/CloseIconButton";
import { usePreventiveMaintenancePlan } from "../hooks/usePreventiveMaintenancePlan";
import { useNumberOfUnits } from "../hooks/useNumberOfUnits";
import NumericInputLabel from "../../../../components/NumericInputLabel";
import { useGetRequiresMaintenance } from "../hooks/useGetRequiresMaintenance";
import SecurityModal from "../../../../components/SecurityModal";

interface HvacProps {
  hvacs?: Array<IHVAC>;
  rowForEdit: ISecurityDetails | undefined;
}
const defaultHvac = [
  {
    parentRow: true,
    tableName: "hvac",
    numberOfUnitsId: null,
  },
] as Array<IHVAC>;

const HvacForm = ({
  rowForEdit,
  hvacs = defaultHvac,
}: HvacProps): JSX.Element => {
  const [isShowHvacModal, setShowHvacModal] = useRecoilState(HvacAddState);
  const [showHvacCardModal, setShowHvacCardModal] =
    useRecoilState(HvacModalState);
  const [isAddState] = useRecoilState(SecurityAddState);
  const [isEditState] = useRecoilState(SecurityEditState);
  const [isAddInEditState, setIsAddInEditState] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(HvacSaving);
  const [showDeactivateModal, setShowDeactivateModal] =
    useRecoilState(hvacDeactivateModal);
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [forDeleteDetails, setForDeleteDetails] = useState();
  const queryClient = useQueryClient();
  const [hasFirstFieldValue, setHasFirstFieldValue] = useState(true);
  const [clickCount, setClickCount] = useState(0);
  const [hvacForEditData, setHvacForEditData] =
    useRecoilState<any>(HvacForEdit);

  //Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_security_add;
  const deletePermission = PERMISSIONS.permission_security_delete;

  //#region Form
  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();

  const formMethods = useFormContext();
  //#endregion
  //#region Add/minus of forms
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "hvac",
  });
  const { data: hvacsData } = useGetHvacsByRecordNumber(
    rowForEdit ? rowForEdit?.recordNumber : 0
  );
  const tableArray = hvacs.length > 0 && watch(`${hvacs[0].tableName}`);

  const controlledFields =
    tableArray &&
    fields.map((field, index) => ({
      ...field,
      ...tableArray[index],
    }));

  //SETTING DEFAULT TABLE VALUES
  useEffect(() => {
    if (hvacs) {
      replace(hvacs);
    }
  }, [hvacs]);
  //#endregion Add/minus
  //#region Options

  // Preventive Maintenance Plan Field Options
  const { data: preventivePlan } = usePreventiveMaintenancePlan();
  const preventiveMaintenanceOptions = preventivePlan?.data?.map(
    (x: { id: number; preventiveMaintenancePlan: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.preventiveMaintenancePlan,
      };
    }
  );

  const { data: numberOfUnits } = useNumberOfUnits();
  const numberOfUnitsOption = numberOfUnits?.data?.map(
    (x: { id: number; numberOfUnits: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.numberOfUnits,
      };
    }
  );

  // For Requires Maintenance Field
  const { data: requiresMaitenanceData } = useGetRequiresMaintenance();
  const requiresMaintenanceOpt = requiresMaitenanceData?.data?.map(
    (x: { requiresMaintenanceId: number; requiresMaintenanceName: string }) => {
      return {
        id: x.requiresMaintenanceId,
        value: x.requiresMaintenanceId,
        label: x.requiresMaintenanceName,
      };
    }
  );

  const { data: Vendors } = useVendors();
  const vendorsCompanyOption = Vendors?.data?.map(
    (x: {
      id: number;
      name: string;
      contact: string;
      email: string;
      city: string;
      accountNumber: string;
      state: string;
      street: string;
      vendorType: string;
      zipcode: string;
      tonerRequestEmail: string;
      tonerRequestPhone: string;
    }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.name,
        email: x.email,
        accountNumber: x.accountNumber,
        address:
          (x.street ? x.street : "") +
          " " +
          (x.city ? x.city : "") +
          " " +
          (x.state ? x.state : "") +
          " " +
          (x.zipcode ? x.zipcode : ""),
        vendorType: x.vendorType,
        trEmail: x.tonerRequestEmail,
        tonerPhone: x.tonerRequestPhone,
        contact: x.contact,
      };
    }
  );
  //#endregion
  //Add Validation
  const checkEmptyFields = (buttonState) => {
    if (buttonState === "add") {
      setClickCount(clickCount + 1);
      watch("hvac[0].numberOfUnitsId")
        ? setHasFirstFieldValue(true)
        : setHasFirstFieldValue(false);
    }
    if (buttonState === "proceed") {
      const fieldLen = controlledFields.length;
      let hasValueCount = 0;

      controlledFields.forEach((obj: IHVAC, i: number) =>
        Object.entries(obj)?.forEach(([key, value]) => {
          if (key === "numberOfUnitsId") {
            if (value) {
              hasValueCount = hasValueCount + 1;
              if (fieldLen === hasValueCount) {
                setShowHvacModal(false);
              }
            } else {
              setClickCount(clickCount + 1);
              toast.fire(
                "Information",
                "Please fill the Number of Units to proceed.",
                "info"
              );
              setHasFirstFieldValue(false);
            }
          }
        })
      );
    }
    if (buttonState === "saveEdit") {
      setClickCount(clickCount + 1);
      watch("numberOfUnitsId")
        ? handleEditSave()
        : toast.fire(
            "Information",
            "Please fill the Number of Units to save.",
            "info"
          );
    }
  };

  useEffect(() => {
    !hasFirstFieldValue &&
      toast.fire(
        "Information",
        "Please fill the Number of Units to add new.",
        "info"
      );
  }, [clickCount]);
  //Single Add Initialization
  const {
    mutate: addSingleHvac,
    isSuccess: addHvacSuccess,
    data: addHvacResult,
    isError: addHvacError,
    reset: resetAddHvacData,
  } = useAddSingleHvac();
  //Single Edit Initialization
  const {
    mutate: editSingleHvac,
    isSuccess: editHvacSuccess,
    data: editHvacResult,
    isError: editHvacError,
    reset: resetEditHvacData,
  } = useEditSingleHvac();
  //Delete Initialization
  const {
    mutate: deleteHvac,
    isSuccess: deleteHvacSuccess,
    data: deleteHvacResult,
    isError: deleteHvacError,
    reset: resetDeleteHvacData,
  } = useDeleteHvac();
  //Reset form data single Add
  const resetData = () => {
    formMethods.setValue("hvac[0].numberOfUnitsId", "");
    formMethods.setValue("hvac[0].unitType", "");
    formMethods.setValue("hvac[0].tonCapacity", "");
    formMethods.setValue("hvac[0].make", "");
    formMethods.setValue("hvac[0].model", "");
    formMethods.setValue("hvac[0].serialNumber", "");
    formMethods.setValue("hvac[0].warrantyCompressor", "");
    formMethods.setValue("hvac[0].warrantyLabor", "");
    formMethods.setValue("hvac[0].hvacinstallDate", "");
    formMethods.setValue("hvac[0].preventiveMaintenancePlanId", "");
    formMethods.setValue("hvac[0].maintenanceCompanyId", "");
    formMethods.setValue("hvac[0].requiresMaintenanceId", "");
    formMethods.setValue("hvac[0].firstMaintenanceDate", "");
    formMethods.setValue("hvac[0].maintenancePeriodicity", "");
  };
  //Save Get values for single add
  const handleAddSave = () => {
    setIsSaving(true);
    const hvacData = {
      recordNumber: rowForEdit?.recordNumber,
      numberOfUnitsId: formMethods.getValues("hvac[0].numberOfUnitsId"),
      unitType: formMethods.getValues("hvac[0].unitType"),
      tonCapacity: formMethods.getValues("hvac[0].tonCapacity"),
      make: formMethods.getValues("hvac[0].make"),
      model: formMethods.getValues("hvac[0].model"),
      serialNumber: formMethods.getValues("hvac[0].serialNumber"),
      warrantyCompressor: formMethods.getValues("hvac[0].warrantyCompressor"),
      warrantyLabor: formMethods.getValues("hvac[0].warrantyLabor"),
      maintenancePeriodicity: formMethods.getValues(
        "hvac[0].maintenancePeriodicity"
      ),
      hvacinstallDate: formMethods.getValues("hvac[0].hvacinstallDate"),
      preventiveMaintenancePlanId: formMethods.getValues(
        "hvac[0].preventiveMaintenancePlanId"
      ),
      preventiveMaintenancePlan: formMethods.getValues(
        "hvac[0].preventiveMaintenancePlan"
      ),
      maintenanceCompanyId: formMethods.getValues(
        "hvac[0].maintenanceCompanyId"
      ),
      requiresMaintenanceId: formMethods.getValues(
        "hvac[0].requiresMaintenanceId"
      ),
      firstMaintenanceDate: formMethods.getValues(
        "hvac[0].firstMaintenanceDate"
      ),
    };
    addSingleHvac(hvacData);
    setShowHvacModal(false);
  };
  //Set Edit default values
  const setEditHvacValues = (hvac) => {
    formMethods.setValue("id", hvac.id);
    formMethods.setValue("numberOfUnitsId", hvac.numberOfUnitsId);
    formMethods.setValue("unitType", hvac.unitType);
    formMethods.setValue("tonCapacity", hvac.tonCapacity);
    formMethods.setValue("make", hvac.make);
    formMethods.setValue("model", hvac.model);
    formMethods.setValue("serialNumber", hvac.serialNumber);
    formMethods.setValue("warrantyCompressor", hvac.warrantyCompressor);
    formMethods.setValue("warrantyLabor", hvac.warrantyLabor);
    formMethods.setValue("requiresMaintenanceId", hvac.requiresMaintenanceId);
    formMethods.setValue("maintenancePeriodicity", hvac.maintenancePeriodicity);
    formMethods.setValue(
      "hvacinstallDate",
      hvac.hvacinstallDate ? hvac.hvacinstallDate?.slice(0, 10) : null
    );
    formMethods.setValue(
      "firstMaintenanceDate",
      hvac.firstMaintenanceDate ? hvac.firstMaintenanceDate?.slice(0, 10) : null
    );
    formMethods.setValue(
      "preventiveMaintenancePlanId",
      hvac.preventiveMaintenancePlanId
    );
    formMethods.setValue("maintenanceCompanyId", hvac.maintenanceCompanyId);
    formMethods.setValue("isActive", hvac.isActive);
    formMethods.setValue("createdBy", hvac.createdBy);
    formMethods.setValue("createdDate", hvac.createdDate);

    queryClient.invalidateQueries("GetHvacsByRecordNumber");
    setShowHvacCardModal(true);
  };

  //Save Edit single door buzzer
  const handleEditSave = () => {
    setIsSaving(true);
    const hvacNewData = {
      recordNumber: rowForEdit?.recordNumber,
      id: formMethods.getValues("id"),
      numberOfUnitsId: formMethods.getValues("numberOfUnitsId"),
      unitType: formMethods.getValues("unitType"),
      tonCapacity: formMethods.getValues("tonCapacity"),
      make: formMethods.getValues("make"),
      model: formMethods.getValues("model"),
      serialNumber: formMethods.getValues("serialNumber"),
      warrantyCompressor: formMethods.getValues("warrantyCompressor"),
      warrantyLabor: formMethods.getValues("warrantyLabor"),
      requiresMaintenanceId: formMethods.getValues("requiresMaintenanceId"),
      maintenancePeriodicity: formMethods.getValues("maintenancePeriodicity"),
      firstMaintenanceDate: formMethods.getValues("firstMaintenanceDate")
        ? formMethods.getValues("firstMaintenanceDate").slice(0, 10)
        : null,
      hvacinstallDate: formMethods.getValues("hvacinstallDate")
        ? formMethods.getValues("hvacinstallDate").slice(0, 10)
        : null,
      preventiveMaintenancePlanId: formMethods.getValues(
        "preventiveMaintenancePlanId"
      ),
      maintenanceCompanyId: formMethods.getValues("maintenanceCompanyId"),
      isActive: formMethods.getValues("isActive"),
      createdBy: formMethods.getValues("createdBy"),
      createdDate: formMethods.getValues("createdDate"),
    };
    const hvacOldData = {
      recordNumber: hvacForEditData?.recordNumber,
      id: hvacForEditData?.id,
      numberOfUnitsId: hvacForEditData?.numberOfUnitsId,
      unitType: hvacForEditData?.unitType,
      tonCapacity: hvacForEditData?.tonCapacity,
      make: hvacForEditData?.make,
      model: hvacForEditData?.model,
      serialNumber: hvacForEditData?.serialNumber,
      warrantyCompressor: hvacForEditData?.warrantyCompressor,
      warrantyLabor: hvacForEditData?.warrantyLabor,
      requiresMaintenanceId: hvacForEditData?.requiresMaintenanceId,
      maintenancePeriodicity: hvacForEditData?.maintenancePeriodicity,
      firstMaintenanceDate: hvacForEditData?.firstMaintenanceDate
        ? hvacForEditData?.firstMaintenanceDate?.slice(0, 10)
        : null,
      hvacinstallDate: hvacForEditData?.hvacinstallDate
        ? hvacForEditData?.hvacinstallDate?.slice(0, 10)
        : null,
      preventiveMaintenancePlanId: hvacForEditData?.preventiveMaintenancePlanId,
      maintenanceCompanyId: hvacForEditData?.maintenanceCompanyId,
      isActive: hvacForEditData?.isActive,
      createdBy: hvacForEditData?.createdBy,
      createdDate: hvacForEditData?.createdDate,
    };
    const newData = JSON.stringify(hvacNewData);
    const oldData = JSON.stringify(hvacOldData);
    if (newData === oldData) {
      toast.fire("Saving Failed", "No changes has been made.", "warning");
      setIsSaving(false);
    } else {
      setIsSaving(true);
      editSingleHvac(hvacNewData);
      setShowHvacCardModal(false);
    }
  };
  //Toasters
  if (addHvacSuccess && addHvacResult) {
    if (addHvacResult.status === 201 || addHvacResult.status === 200) {
      toast.fire("Success", "HVAC added successfully.", "success");
      queryClient.invalidateQueries("GetHvacsByRecordNumber");
    }
    setIsSaving(false);
    resetAddHvacData();
    resetData();
  }
  if (editHvacSuccess && editHvacResult) {
    if (editHvacResult.status === 201 || editHvacResult.status === 200) {
      toast.fire("Success", "HVAC updated successfully.", "success");
      queryClient.invalidateQueries("GetHvacsByRecordNumber");
    }
    setIsSaving(false);
    resetEditHvacData();
  }
  if (deleteHvacSuccess && deleteHvacResult) {
    if (deleteHvacResult.status === 201 || deleteHvacResult.status === 200) {
      toast.fire("Success", "HVAC deactivated successfully.", "success");
      queryClient.invalidateQueries("GetHvacsByRecordNumber");
    }
    resetDeleteHvacData();
  }
  //Deactivate
  const handleDeactivate = () => {
    deleteHvac(forDeleteDetails);
    setShowDeactivateModal(false);
  };
  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {"Are you sure you want to deactivate this HVAC?"}
    </div>
  );
  useEffect(() => {
    if (userPermissions.includes(deletePermission)) {
      setHasDeletePermission(true);
    }
  }, []);

  return (
    <div className="mx-2 mt-2 bg-white box-shadow rounded-lg h-full">
      <div className="flex flex-row justify-between py-2">
        <div className="text-quinary font-bold font-sans px-4">
          HVAC Section
        </div>
        {userPermissions.includes(addPermission) ? (
          <div className="py-2 px-4">
            <AddButton
              className={"opacity-100"}
              disabled={
                hvacsData !== undefined
                  ? hvacsData.length >= 5
                    ? true
                    : false
                  : false
              }
              buttonText="Add HVAC"
              onClick={() => {
                setShowHvacModal(true);
                setIsAddInEditState(true);
              }}
            />
          </div>
        ) : (
          <div className="py-5 px-4"></div>
        )}
      </div>
      {isAddState && (
        <div className="text-quinary font-bold font- py-2 px-4">
          <h1
            className="mr-auto truncate w-full cursor-pointer text-ellipsis font-bold text-primary underline text-lg uppercase"
            onClick={() => {
              setShowHvacModal(true);
            }}
          >
            {controlledFields &&
              controlledFields[0]?.numberOfUnitsId &&
              "View HVAC/s"}
          </h1>
        </div>
      )}
      {/* Show card edit button if edit state*/}
      {isEditState && (
        <div className="w-full borderContainer -mt-4 pb-4 bg-white px-4">
          <div className="grid gap-2 grid-cols-5">
            {/* Card */}
            {hvacsData?.map((hvac) => (
              <div key={hvac.id}>
                <HvacCard
                  hvac={hvac}
                  isEditState={isEditState}
                  onClickView={() => {}}
                  onClickEdit={() => {
                    setEditHvacValues(hvac);
                    setHvacForEditData(hvac);
                  }}
                  onClickDelete={() => {
                    setShowDeactivateModal(true);
                    setForDeleteDetails(hvac);
                  }}
                  hasDeletePermission={hasDeletePermission}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Add HVAC Form */}
      {isShowHvacModal && (
        <SecurityModal
          isCloseHide={true}
          modalFilterStat=""
          className=""
          width={""}
          title={""}
          isOpen={isShowHvacModal}
          onClose={undefined}
          hasCloseButton={false}
        >
          <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
            <div className="ml-2 font-bold text-primary text-md">
              {isAddState || isAddInEditState ? "Add HVAC" : "Edit HVAC"}
            </div>
            {isAddState ? (
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={"Proceed"}
                  className={undefined}
                  onClick={() => {
                    checkEmptyFields("proceed");
                    setHasFirstFieldValue(true);
                  }}
                />
                {/* <CancelButton
                  onClick={() => {
                    setShowHvacModal(false);
                    resetData();
                    remove([1, 2, 3, 4]);
                  }}
                /> */}
                <div className="relative -top-2 -right-6">
                  <CloseIconButton onClick={() => setShowHvacModal(false)} />
                </div>
              </div>
            ) : (
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={() => {
                    watch("hvac[0].numberOfUnitsId")
                      ? handleAddSave()
                      : toast.fire(
                          "Information",
                          "Please fill the number of units field to save.",
                          "info"
                        );
                  }}
                />
                <CancelButton
                  onClick={() => {
                    setShowHvacModal(false);
                    resetData();
                  }}
                />
              </div>
            )}
          </div>
          <div className="w-full border-b-2 border-secondary mb-3"></div>
          <div className="text-xs text-secondary italic font-normal m-2">
            <b> Note:</b> All fields with * are required to fill.
          </div>
          <div className="box-shadow rounded-md modal-content">
            {controlledFields &&
              controlledFields.map((field, index) => {
                return (
                  <>
                    <div className="ml-4 py-2" key={field.id}>
                      {index > 0 ? (
                        <MinusButton
                          type="button"
                          onClick={() => remove(index)}
                        />
                      ) : (
                        !isEditState && (
                          <PlusButton
                            type="button"
                            onClick={(e) => {
                              if (controlledFields.length < 5) {
                                checkEmptyFields("add");
                                watch(`hvac[0].numberOfUnitsId`) &&
                                  append({
                                    tableName: hvacs[0]?.tableName,
                                    parentRow: false,
                                  });
                              }
                            }}
                          />
                        )
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 mx-4 gap-6 pb-4">
                      <div className="space-y-1">
                        <Controller
                          name={`${hvacs[0].tableName}[${index}].numberOfUnitsId`}
                          control={formMethods.control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={numberOfUnitsOption}
                              label="Number of Units"
                              className={
                                formMethods.formState.errors.numberOfUnitsId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={true}
                            />
                          )}
                        />
                        {/* For Clarification */}
                        <TextInputLabelHookForm
                          name={`${hvacs[0].tableName}[${index}].unitType`}
                          label="Unit Type"
                          type="text"
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].unitType`,
                            {}
                          )}
                          maxLength={50}
                        />
                        <TextInputLabelHookForm
                          name={`${hvacs[0].tableName}[${index}].tonCapacity`}
                          label="Ton Capacity"
                          type="text"
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].tonCapacity`,
                            {}
                          )}
                          maxLength={100}
                        />
                        <TextInputLabelHookForm
                          name={`${hvacs[0].tableName}[${index}].make`}
                          label="Make"
                          type="text"
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].make`,
                            {}
                          )}
                          maxLength={100}
                        />
                        <TextInputLabelHookForm
                          name={`${hvacs[0].tableName}[${index}].model`}
                          label="Model"
                          type="text"
                          onChange={(e) => {
                            if (e.target.value) {
                              var x = e.target.value
                                .replace(/[^A-Za-z0-9]/g, "")
                                .match(/^[a-zA-Z0-9]+$/);
                              e.target.value = x?.toString() ?? "";
                            }
                          }}
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].model`,
                            {}
                          )}
                          maxLength={100}
                        />
                        <TextInputLabelHookForm
                          name={`${hvacs[0].tableName}[${index}].serialNumber`}
                          label="Serial Number"
                          type="text"
                          onChange={(e) => {
                            if (e.target.value) {
                              var sNumber = e.target.value
                                .replace(/[^A-Za-z0-9]/g, "")
                                .match(/^[a-zA-Z0-9]+$/);
                              e.target.value = sNumber?.toString() ?? "";
                            }
                          }}
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].serialNumber`,
                            {}
                          )}
                          maxLength={100}
                        />
                        <NumericInputLabel
                          name={`${hvacs[0].tableName}[${index}].warrantyCompressor`}
                          label="Warranty Compressor"
                          type="text"
                          onChange={(e) => {
                            if (e.target.value) {
                              var warrantyComp = e.target.value
                                .replace(/\D/g, "")
                                .match(/(\d+)/);
                              e.target.value =
                                warrantyComp !== null ? warrantyComp[0] : "";
                            }
                          }}
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].warrantyCompressor`,
                            {}
                          )}
                          maxLength={100}
                        />
                        <NumericInputLabel
                          name={`${hvacs[0].tableName}[${index}].warrantyLabor`}
                          label="Warranty Labor"
                          type="text"
                          onChange={(e) => {
                            if (e.target.value) {
                              var warrantyLbr = e.target.value
                                .replace(/\D/g, "")
                                .match(/(\d+)/);
                              e.target.value =
                                warrantyLbr !== null ? warrantyLbr[0] : "";
                            }
                          }}
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].warrantyLabor`,
                            {}
                          )}
                          maxLength={100}
                        />
                      </div>
                      <div className="space-y-1">
                        <TextInputLabelHookForm
                          name={`${hvacs[0].tableName}[${index}].hvacinstallDate`}
                          label="Install Date"
                          type="date"
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].hvacinstallDate`,
                            {}
                          )}
                        />
                        <Controller
                          name={`${hvacs[0].tableName}[${index}].preventiveMaintenancePlanId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={preventiveMaintenanceOptions}
                              label="Preventive Maintenance Plan"
                              className={
                                formMethods.formState.errors
                                  .preventiveMaintenancePlan &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <Controller
                          name={`${hvacs[0].tableName}[${index}].maintenanceCompanyId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={vendorsCompanyOption}
                              label="Maintenance Company"
                              className={
                                formMethods.formState.errors
                                  .maintenanceCompany &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <Controller
                          name={`${hvacs[0].tableName}[${index}].requiresMaintenanceId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={requiresMaintenanceOpt}
                              label="Requires Maintenance"
                              className={
                                formMethods.formState.errors
                                  .requiresMaintenanceName &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <TextInputLabelHookForm
                          name={`${hvacs[0].tableName}[${index}].firstMaintenanceDate`}
                          label="First Maintenance Date"
                          type="date"
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].firstMaintenanceDate`,
                            {}
                          )}
                        />
                        <TextInputLabelHookForm
                          name={`${hvacs[0].tableName}[${index}].maintenancePeriodicity`}
                          label="Maintenance Periodicity"
                          type="text"
                          register={formMethods.register(
                            `${hvacs[0].tableName}[${index}].maintenancePeriodicity`,
                            {}
                          )}
                          maxLength={100}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="w-full border-b-2 border-grey-200"></div>
                    </div>
                  </>
                );
              })}
          </div>
        </SecurityModal>
      )}
      {/* Edit HVAC Card Form */}
      {showHvacCardModal && (
        <Modal
          isCloseHide={true}
          modalFilterStat=""
          className=""
          width={""}
          title={""}
          isOpen={showHvacCardModal}
          onClose={undefined}
          hasCloseButton={false}
        >
          {" "}
          <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
            <div className="ml-2 font-bold text-primary text-md">
              {isAddState ? "Add HVAC" : "Edit HVAC"}
            </div>
            <div className="flex justify-between gap-2 mr-2">
              <SubmitHookForm
                type="button"
                disabled={false}
                buttonText={isSaving ? "Saving.." : "Save"}
                className={undefined}
                onClick={() => {
                  checkEmptyFields("saveEdit");
                }}
              />
              <CancelButton
                onClick={() => {
                  setShowHvacCardModal(false);
                }}
              />
            </div>
          </div>
          <div className="w-full border-b-2 border-secondary mb-3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 mx-4 mr-5 pb-2 lg:lg:gap-12 gap-2 gap-2">
            <div className="space-y-1 mr-6">
              <Controller
                name="numberOfUnitsId"
                control={formMethods.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={numberOfUnitsOption}
                    label="Number of Units"
                    className={
                      formMethods.formState.errors.numberOfUnitsId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={true}
                  />
                )}
              />
              {/* For Clarification */}
              <TextInputLabelHookForm
                name="unitType"
                label="Unit Type"
                type="text"
                register={formMethods.register("unitType", {})}
                maxLength={50}
              />
              <TextInputLabelHookForm
                name="tonCapacity"
                label="Ton Capacity"
                type="text"
                register={formMethods.register("tonCapacity", {})}
                maxLength={100}
              />
              <TextInputLabelHookForm
                name="make"
                label="Make"
                type="text"
                register={formMethods.register("make", {})}
                maxLength={100}
              />
              <TextInputLabelHookForm
                name="model"
                label="Model"
                type="text"
                onChange={(e) => {
                  if (e.target.value) {
                    var x = e.target.value
                      .replace(/[^A-Za-z0-9]/g, "")
                      .match(/^[a-zA-Z0-9]+$/);
                    e.target.value = x?.toString() ?? "";
                  }
                }}
                register={formMethods.register("model", {})}
                maxLength={100}
              />
              <TextInputLabelHookForm
                name="serialNumber"
                label="Serial #"
                type="text"
                onChange={(e) => {
                  if (e.target.value) {
                    var x = e.target.value
                      .replace(/[^A-Za-z0-9]/g, "")
                      .match(/^[a-zA-Z0-9]+$/);
                    e.target.value = x?.toString() ?? "";
                  }
                }}
                register={formMethods.register("serialNumber", {})}
                maxLength={100}
              />
              <NumericInputLabel
                name="warrantyCompressor"
                label="Warranty Compressor"
                type="text"
                onChange={(e) => {
                  if (e.target.value) {
                    var warrantyComp = e.target.value
                      .replace(/\D/g, "")
                      .match(/(\d+)/);
                    e.target.value =
                      warrantyComp !== null ? warrantyComp[0] : "";
                  }
                }}
                register={formMethods.register("warrantyCompressor", {})}
                maxLength={100}
              />
              <NumericInputLabel
                name="warrantyLabor"
                label="Warranty Labor"
                type="text"
                onChange={(e) => {
                  if (e.target.value) {
                    var warrantyLbr = e.target.value
                      .replace(/\D/g, "")
                      .match(/(\d+)/);
                    e.target.value = warrantyLbr !== null ? warrantyLbr[0] : "";
                  }
                }}
                register={formMethods.register("warrantyLabor", {})}
                maxLength={100}
              />
            </div>
            <div className="space-y-1 mr-6">
              <TextInputLabelHookForm
                name="hvacinstallDate"
                label="Install Date"
                type="date"
                register={formMethods.register("hvacinstallDate", {})}
              />
              <Controller
                name="preventiveMaintenancePlanId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={preventiveMaintenanceOptions}
                    label="Preventive Maintenance Plan"
                    className={
                      formMethods.formState.errors.preventiveMaintenancePlan &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <Controller
                name="maintenanceCompanyId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={vendorsCompanyOption}
                    label="Maintenance Company"
                    className={
                      formMethods.formState.errors.maintenanceCompany &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <Controller
                name="requiresMaintenanceId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={requiresMaintenanceOpt}
                    label="Requires Maintenance"
                    className={
                      formMethods.formState.errors.requiresMaintenanceName &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <TextInputLabelHookForm
                name="firstMaintenanceDate"
                label="First Maintenance Date"
                type="date"
                register={formMethods.register("firstMaintenanceDate", {})}
              />
              <TextInputLabelHookForm
                name="maintenancePeriodicity"
                label="Maintenance Periodicity"
                type="text"
                register={formMethods.register("maintenancePeriodicity", {})}
                maxLength={100}
              />
              {isEditState && hasDeletePermission && (
                <CheckBoxLabelHookForm
                  name="isActive"
                  type="checkbox"
                  register={register}
                  label="Active Account"
                  className="cursor-pointer auto-rows-auto text-right justify-items-center"
                />
              )}
            </div>
          </div>
        </Modal>
      )}
      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <ConfirmBoxDelete
          ModalTitle="Security HVAC"
          qConfirmation={deleteMessage}
          isOpen={showDeactivateModal}
          onClose={() => setShowDeactivateModal(false)}
          onClickFunction={() => handleDeactivate()}
          cancelButtonRef={undefined}
        />
      )}
    </div>
  );
};

export default HvacForm;
